import { useCallback } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type ScopedPermissions } from '@air/api/types';
import { useAirModal } from '@air/provider-modal';

import { SharePrivateClipModal } from '~/components/SharePrivateClipModal/SharePrivateClipModal';
import { type AssetShareLinkModalClip } from '~/components/SharePrivateClipModal/types';
import { usePrivateAssetOpenLocation } from '~/hooks/usePrivateAssetOpenLocation';
import { canViewShortUrl } from '~/utils/permissions/shortUrlPermissions';

export const canSharePrivateAsset = (clipPermissions: ScopedPermissions | undefined) =>
  canViewShortUrl(clipPermissions);

type UseSharePrivateAssetParamsBase = {
  clipPermissions?: ScopedPermissions | undefined;
};

type UseSharePrivateAssetWithTrackLocation = UseSharePrivateAssetParamsBase & {
  trackLocation: AirActionTrackingLocation;
};

type UseSharePrivateAssetWithoutTrackLocation = UseSharePrivateAssetParamsBase & {
  trackLocation?: undefined;
};

type UseSharePrivateAssetReturnWithTrackLocation = {
  canShare: boolean;
  openShareModal: (clip: AssetShareLinkModalClip) => void;
};

type UseSharePrivateAssetReturnWithoutTrackLocation = {
  canShare: boolean;
  openShareModal: (clip: AssetShareLinkModalClip, trackLocation: AirActionTrackingLocation) => void;
};

export function useSharePrivateAsset(
  params: UseSharePrivateAssetWithTrackLocation,
): UseSharePrivateAssetReturnWithTrackLocation;

export function useSharePrivateAsset(
  params?: UseSharePrivateAssetWithoutTrackLocation,
): UseSharePrivateAssetReturnWithoutTrackLocation;

export function useSharePrivateAsset({
  clipPermissions,
  trackLocation,
}: UseSharePrivateAssetWithTrackLocation | UseSharePrivateAssetWithoutTrackLocation = {}):
  | UseSharePrivateAssetReturnWithTrackLocation
  | UseSharePrivateAssetReturnWithoutTrackLocation {
  const [sharePrivateClip] = useAirModal(SharePrivateClipModal);
  const canShare = canSharePrivateAsset(clipPermissions);
  const analyticsLocation = usePrivateAssetOpenLocation();

  const openShareModal = useCallback(
    (clip: AssetShareLinkModalClip, callTrackLocation?: AirActionTrackingLocation) => {
      if (!clip.id || !clip.assetId) {
        return;
      }

      const resolvedTrackLocation = callTrackLocation ?? trackLocation;

      if (!resolvedTrackLocation) {
        return;
      }

      sharePrivateClip({
        analyticsLocation,
        clip,
        trackLocation: resolvedTrackLocation,
      });
    },
    [analyticsLocation, sharePrivateClip, trackLocation],
  );

  return {
    canShare,
    openShareModal,
  };
}
